import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Text } from 'rebass/styled-components';
import { useDebounceCallback } from '@react-hook/debounce';
import {
  Row,
  PolicyEditor,
  Section,
  Heading,
  Stepper,
  Loading,
  Subtitle,
  Link,
  NavigationLink,
  Pagination,
  PolicyReorder,
  ResultsEmpty,
  SearchInputV2,
  Button,
  ConfirmModal,
  NavigateGuard,
} from '..';
import api from '../../api';
import validation from '../../validation';
import EditorError from '../Editor/EditorError';
import { parseToArray, checkValueType } from '../Editor/util';
import { ReactComponent as IconBack } from '../../assets/back-icon.svg';
import {
  convertV2PolicyToV1, convertV1PolicyToV2, formatV2Condition, convertV2ConditionToV1Ui,
} from './util';
import PolicyManagementSection from './policy-management-section';
import PolicyListHeader from './policy-list-header';
import { useAppState, useNotification } from '../../hooks';
import DefaultButtons from '../custom-context/default-buttons';
import { PolicyAnalyticsProvider } from '../../providers';

const LINK_TO_DOCS = 'https://developers.liveperson.com/conversation-orchestrator-dynamic-routing-creating-and-managing-policies.html';
const ANALYTICS_TIME_INTERVAL = '30d';

const parseValueToArray = (obj) => {
  const newObject = { ...obj };
  if (Array.isArray(newObject.conditions)) {
    newObject.conditions = newObject.conditions.map((item) => {
      const newItem = { ...item };
      // We only want to parse the value if it's a list
      if (checkValueType(item.value) === 'list') {
        newItem.value = parseToArray(newItem.value);
      }
      return newItem;
    });
  }
  return newObject;
};

const parseConditionsValueToArrays = (policy) => {
  let updatedPolicy = { ...policy };
  updatedPolicy = parseValueToArray(updatedPolicy);
  return updatedPolicy;
};

function PoliciesCcsV2(props) {
  const {
    policyName, type, subtitle,
  } = props;

  const isMounted = useRef(false);
  const currentItem = useRef(null);

  const [policies, setPolicies] = useState([]);
  // policy for editing
  const [temporaryPolicy, setTemporaryPolicy] = useState({});
  // policy for viewing detail
  const [selectedPolicy, setSelectedPolicy] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFetchingPolicies, setIsFetchingPolicies] = useState(true);
  const [isReordering, setIsReordering] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [noOfRecordsPerPage, setNoOfRecordsPerPage] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState();
  const [overallSelectedPolicyOrder, setOverallSelectedPolicyIndex] = useState(0);
  const [totalPolicyCount, setTotalPolicyCount] = useState(0);
  const [policyError, setPolicyError] = useState('');
  const [confirmModalProps, setConfirmModalProps] = useState({ show: false });
  const [isComplexConditionEditor, setComplexConditionEditor] = useState(false);
  const {
    features, isLoading: isAppStateLoading, accountId: brandId, themeMode,
  } = useAppState();
  const { addNotification } = useNotification();

  const focusPolicy = useCallback((v2Policy) => {
    if (v2Policy) {
      if (features['maven.feature.textareaEditor']) { // checking switch for complex policies
        if (v2Policy.schemaVersion === '3') {
          setComplexConditionEditor(true);
        }
        setSelectedPolicy(v2Policy);
      } else {
        try {
          const v1Policy = convertV2PolicyToV1(v2Policy);
          setComplexConditionEditor(false);
          setSelectedPolicy(v1Policy);
          setPolicyError('');
        } catch (err) {
          setComplexConditionEditor(true);
          setSelectedPolicy(v2Policy);
        }
      }
    } else {
      setSelectedPolicy({});
    }
  }, [features]);

  const refreshSelectedPolicy = useCallback(() => {
    const index = policies.findIndex((policy) => policy.docId === selectedPolicy.docId);
    focusPolicy(policies[Math.max(0, index)]);
  }, [focusPolicy, policies, selectedPolicy.docId]);

  const fetchSelectedPolicy = useCallback(() => {
    const index = policies.findIndex((policy) => policy.docId === selectedPolicy.docId);
    return policies[Math.max(0, index)];
  }, [policies, selectedPolicy.docId]);

  const isItemSelected = useCallback((item) => {
    if (isEditing) {
      return temporaryPolicy
        && Object.keys(temporaryPolicy).length > 0
        && temporaryPolicy.docId === item.docId;
    }
    return selectedPolicy
      && Object.keys(selectedPolicy).length > 0
      && selectedPolicy.docId === item.docId;
  }, [isEditing, selectedPolicy, temporaryPolicy]);

  /**
   * searchPolicies
   *
   * This is only called directly to refresh after a user CRUD operation, eg. Save, delete,
   * cancel, reorder.
   * We call a debounced callback in order to handle user search.
   */
  const searchPolicies = useCallback(async (
    isCreatingOrDeletingOrSearching, fetchPaginationMetadata = true,
  ) => {
    if (isMounted.current) {
      setIsFetchingPolicies(true);

      try {
        const data = await api.ccsV2.searchPolicy(
          brandId,
          {
            name: searchInput, noOfRecordsPerPage, pageNumber, fetchPaginationMetadata,
          },
        );

        if (isMounted.current) {
          setPolicies(data.docs);

          if (fetchPaginationMetadata) {
            setPageNumber(data.page);
            setTotalPages(data.totalPages);
            setTotalPolicyCount(data.totalDocs);
          }

          if (isCreatingOrDeletingOrSearching) {
            focusPolicy(data.docs[0]);
          }
        }
      } catch (error) {
        if (isMounted.current) {
          addNotification(error, 'error');
        }
      }
      if (isMounted.current) {
        setIsFetchingPolicies(false);
      }
    }
  }, [brandId, searchInput, pageNumber, noOfRecordsPerPage]); // eslint-disable-line
  // TODO: avoid disabling this lint rule

  const confirmDeletePolicy = useCallback(async () => {
    if (isMounted.current) {
      setConfirmModalProps({
        show: false,
      });
      setIsDeleting(true);
      try {
        await api.ccsV2.deletePolicy(brandId, selectedPolicy.docId);
        if (policies.length === 1 && pageNumber !== 1) {
          setPageNumber(pageNumber - 1);
        } else {
          await searchPolicies(true);
        }
        addNotification('Policy deleted successfully', 'success');
      } catch (error) {
        if (isMounted.current) {
          addNotification(error, 'error');
        }
      }
      if (isMounted.current) {
        setIsEditing(false);
        setIsDeleting(false);
      }
    }
  }, [addNotification, brandId, pageNumber, policies.length, searchPolicies, selectedPolicy.docId]);

  const deletePolicy = useCallback(async () => {
    if (!isDeleting) {
      if (isMounted.current) {
        setConfirmModalProps({
          show: true,
          detail: `Are you sure you want to delete the policy "${selectedPolicy.name}"`,
          onConfirm: confirmDeletePolicy,
          onCancel: () => {
            if (isMounted.current) {
              setConfirmModalProps({
                show: false,
              });
            }
          },
        });
      }
    }
  }, [isDeleting, selectedPolicy.name, confirmDeletePolicy]);

  const editPolicy = useCallback(() => {
    if (!policyError) {
      // Strip out unwanted properties for edit mode
      const newPolicy = {
        docId: selectedPolicy.docId,
        name: selectedPolicy.name,
        actions: selectedPolicy.actions,
        enabled: selectedPolicy.enabled,
        priority: selectedPolicy.priority,
      };

      if (features['maven.feature.textareaEditor']) { // checking switch for complex policies
        if (selectedPolicy.schemaVersion === '3') {
          newPolicy.condition = selectedPolicy.condition;
          setComplexConditionEditor(true);
        } else {
          try {
            const { condition, ...otherProps } = selectedPolicy;
            const conditionArrayV1 = convertV2ConditionToV1Ui(condition);
            newPolicy.conditions = conditionArrayV1 || condition;
            setComplexConditionEditor(false);
          } catch (err) {
            newPolicy.schemaVersion = '3';
            setComplexConditionEditor(true);
          }
        }
      } else {
        newPolicy.conditions = selectedPolicy.conditions;
        // Add in an empty condition if there is none in the policy
        if (newPolicy.conditions && newPolicy.conditions.length === 0) {
          newPolicy.conditions = [{ value: '', type: 'EQUAL', property: '' }];
        }
      }

      // Add in an empty action if there is none in the policy
      if (newPolicy.actions.length === 0) {
        newPolicy.actions = [{ type: 'SEND_MESSAGE', payload: { message: '' } }];
      }

      if (isMounted.current) {
        setTemporaryPolicy(newPolicy);
        setPolicyError('');
        setIsEditing(true);
      }
    }
  }, [selectedPolicy, policyError, features]);

  const validatePolicy = (obj) => {
    // Remove the isTemporary, placeHolderText, and isUntitled properties
    // so that validation doesn't bork
    // these properties don't need to be validated because they are more for UI management.
    const {
      isUntitled,
      placeHolderText,
      isTemporary,
      accountId,
      docId,
      schemaVersion,
      priority,
      ...cleanedPolicy
    } = obj;

    // Removing previous error keys
    const cleanedTemporaryPolicy = validation.removeErrorKeysFromJson(cleanedPolicy);

    // Validate
    const {
      passedValidation, validationErrors,
    } = validation.validatePolicy(cleanedPolicy, true, isComplexConditionEditor);

    setIsValid(passedValidation);

    if (passedValidation) {
      // Set object WITHOUT error keys
      setTemporaryPolicy({
        isTemporary,
        docId,
        priority,
        ...cleanedTemporaryPolicy,
      });
      setPolicyError('');
    } else {
      // Add in the error properties
      const cleanedPolicyWithErrors = validation.addPolicyErrorProperty(
        cleanedPolicy, validationErrors,
      );

      // Set object WITH error keys
      setTemporaryPolicy({
        isTemporary, accountId, docId, schemaVersion, priority, ...cleanedPolicyWithErrors,
      });
      setPolicyError('');
    }

    return passedValidation;
  };

  const removeEmptyFallbackSkills = (policy) => {
    const policyWithoutEmptyFallback = { ...policy };
    policyWithoutEmptyFallback.actions.forEach((item, i) => {
      if (item.type === 'TRANSFER_TO_AGENT' && (!item.payload.skillId || item.payload.skillId?.replace(/{{}}/gi, '') === '')) {
        policyWithoutEmptyFallback.actions[i].payload.skillId = '-1';
      }
    });
    return policyWithoutEmptyFallback;
  };

  const tryToSavePolicy = useCallback(async (policy) => {
    try {
      const {
        isTemporary, priority, docId, errorArray, ...otherProps
      } = policy;

      const prevPriority = fetchSelectedPolicy().priority;

      let policyV2;
      let schemaVersion;
      if (isComplexConditionEditor) {
        policyV2 = otherProps;
        schemaVersion = '3';
      } else {
        policyV2 = convertV1PolicyToV2({ ...otherProps });
        schemaVersion = '2';
      }

      const newPolicy = {
        ...policyV2,
        docId: (!docId ? '' : docId),
        accountId: brandId,
        schemaVersion,
        tenantId: brandId,
        condition: formatV2Condition(policyV2.condition),
      };

      if (isTemporary && newPolicy.docId === '') {
        await api.ccsV2.createPolicy(brandId, newPolicy);
      } else {
        await api.ccsV2.updatePolicy(brandId, { ...newPolicy, priority: prevPriority });
      }

      if (isMounted.current) {
        addNotification('Policy Saved', 'success');
        setSelectedPolicy(newPolicy);
        setPolicyError('');
        if (isTemporary) {
          if (pageNumber === 1) {
            await searchPolicies(isTemporary);
          } else {
            // TODO: setting a different page number should trigger the debounced search call
            setPageNumber(1);
          }
        } else {
          await searchPolicies(isTemporary, false);
        }
      }
      if (isMounted.current) {
        setIsEditing(false);
        setTemporaryPolicy({});
        setPolicyError('');
        focusPolicy(newPolicy);
      }
    } catch (error) {
      if (isMounted.current) {
        // Reset object on error
        addNotification(error, 'error');
        setTemporaryPolicy(policy);
        setPolicyError('');
      }
    }
  }, [addNotification, brandId, searchPolicies, pageNumber,
    fetchSelectedPolicy, isComplexConditionEditor, focusPolicy]);

  const validateBeforeSavingPolicy = useCallback(async () => {
    const parsedConditionsPolicy = parseConditionsValueToArrays(temporaryPolicy);
    const newPolicy = removeEmptyFallbackSkills(parsedConditionsPolicy);
    const passedValidation = validatePolicy(newPolicy);
    if (passedValidation) {
      await tryToSavePolicy(newPolicy);
    } else if (isMounted.current) {
      // If the validation does not pass, set the temporaryPolicy back to the correct version
      setTemporaryPolicy(temporaryPolicy);
      setPolicyError('');
    }
  }, [temporaryPolicy, tryToSavePolicy]); // eslint-disable-line

  const savePolicy = useCallback(async () => {
    if (!isSaving) {
      setIsSaving(true);
      await validateBeforeSavingPolicy();

      if (isMounted.current) {
        setIsSaving(false);
      }
    }
  }, [isSaving, validateBeforeSavingPolicy]);

  const addPolicy = useCallback(() => {
    const newPolicy = {
      name: 'New policy',
      enabled: false,
      conditions: [{ value: '', type: 'EQUAL', property: '' }],
      actions: [{ type: 'SEND_MESSAGE', payload: { message: '' } }],
      isTemporary: true,
      isUntitled: true,
      placeHolderText: 'Enter a new policy name',
    };
    if (isMounted.current) {
      setPolicies([newPolicy, ...policies]);
      setTemporaryPolicy(newPolicy);
      setPolicyError('');
      setIsEditing(true);
      setIsValid(false);
      setComplexConditionEditor(false);
    }
  }, [policies]);

  const cancelPolicy = useCallback(async () => {
    // TODO: Assume we still need to clean policy errors?
    // Reset the error values to a clean state
    // cleanPolicyErrors();

    if (isMounted.current) {
      setIsEditing(false);
      setTemporaryPolicy({});
      setIsValid(true);
      setPolicyError('');
    }
    if (policies.length > 1) {
      refreshSelectedPolicy();
    }
    // Refetch the selected policy
    await searchPolicies(false, false);
  }, [searchPolicies, refreshSelectedPolicy, policies]);

  const handlePolicyClick = useCallback((policy) => {
    if (!isEditing && isMounted.current) {
      focusPolicy(policy);
    }
  }, [focusPolicy, isEditing]);

  const handleToggleChange = useCallback(
    async (policy, value, setIsTogglingSwitch, isSwitchMounted) => {
      const originalPolicies = [...policies];
      if (!isEditing) {
        let newPolicy;
        if (isMounted.current) {
          setPolicies(policies.map((item) => {
            if (item.docId === policy.docId) {
              newPolicy = { ...item, enabled: value };
              focusPolicy(newPolicy);
              return newPolicy;
            }
            return item;
          }));
        }
        try {
          await api.ccsV2.updatePolicy(brandId, newPolicy);
        } catch (error) {
          if (isMounted.current) {
            setPolicies(originalPolicies);
            addNotification(error, 'error');
          }
        }

        // separate billing metrics into a separate block so that the UI accurately
        // reflects the success or failure of the update call regardless of the
        // metrics call.
        // try {
        //   // TODO: Is this still relevant?
        //   if (features['maven.feature.billingMetrics']) {
        //     await api.policyBillingMetric.callPolicyMetric(brandId, newPolicy);
        //   }
        // } catch (error) {
        //   // do nothing
        // }
      }
      if (isSwitchMounted) {
        setIsTogglingSwitch(false);
      }
    }, [addNotification, brandId, focusPolicy, isEditing, policies],
  );

  const handleOnClose = () => {
    if (isMounted.current) {
      setIsFetchingPolicies(true);
      setSearchInput('');
      setPageNumber(1);
    }
  };

  const debounceSearchPolicies = useDebounceCallback(searchPolicies, 500);

  const handleSearchInputChange = (event) => {
    if (isMounted.current) {
      setSearchInput(event.target.value);
      setPageNumber(1);
    }
  };

  const handlePageChange = (page) => {
    if (isMounted.current) {
      setPageNumber(page);
    }
  };

  const handleRecordsPerPage = (numberOfRecordsPerPage) => {
    if (isMounted.current) {
      setNoOfRecordsPerPage(numberOfRecordsPerPage);
      setPageNumber(1);
    }
  };

  const handlePolicyReorder = (direction) => {
    setIsReordering(direction);
  };

  const handleReorderComplete = useCallback(
    (success, topBoundryCondition, bottomBoundryCondition) => {
      if (success) {
        if (topBoundryCondition) {
          // If we are moving 'up' the first condition on a page
          setPageNumber((prevState) => Math.max(prevState - 1), 1);
        } else if (bottomBoundryCondition) {
          // If we are moving 'down' the last condition on a page
          setPageNumber((prevState) => Math.min(prevState + 1), totalPages);
        } else {
          // All other cases
          searchPolicies(false, false);
        }
      }

      setIsReordering('');
    }, [searchPolicies, totalPages],
  );

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isReordering !== '') {
      searchPolicies(false);
    } else {
      debounceSearchPolicies(true);
    }
  }, [pageNumber, debounceSearchPolicies, searchPolicies]); // eslint-disable-line

  useEffect(() => {
    debounceSearchPolicies(true);
  }, [brandId, searchInput, noOfRecordsPerPage, debounceSearchPolicies]);

  useEffect(() => {
    let currentPagePolicyOrder = 0;
    for (let i = 0; i < policies.length; i++) { // eslint-disable-line
      if (selectedPolicy.docId === policies[i].docId) {
        currentPagePolicyOrder = i + 1;
        break;
      }
    }
    const policyOrder = currentPagePolicyOrder + ((pageNumber - 1) * noOfRecordsPerPage);
    setOverallSelectedPolicyIndex(policyOrder);
  }, [selectedPolicy, policies, pageNumber, noOfRecordsPerPage]);

  useEffect(() => {
    if (!isFetchingPolicies && currentItem.current && typeof currentItem.current.scrollIntoView === 'function') {
      currentItem.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [isFetchingPolicies]);

  const editModeButtons = (
    <Flex justifyContent="space-between" flex={1} ml={2}>
      <Flex flex={1} mr={2} alignItems="center">
        <Button
          variant="secondary"
          mr={3}
          onClick={cancelPolicy}
          disabled={!!isSaving}
          data-testid="cancel-button"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={savePolicy}
          disabled={!isValid}
          data-testid={`save-button${isSaving ? '-enabled' : ''}`}
        >
          {isSaving ? 'Saving' : 'Save'}
        </Button>
        <EditorError isValid={isValid} />
      </Flex>
    </Flex>
  );

  const defaultButtons = selectedPolicy && Object.keys(selectedPolicy).length > 0 ? (
    <DefaultButtons
      editContext={editPolicy}
      deleteContext={deletePolicy}
      isDeleting={isDeleting}
      disabled={!!policyError}
    />
  ) : null;

  const addPolicyButton = (
    <Button
      variant="primary"
      onClick={addPolicy}
      disabled={isEditing}
      data-testid="add-policy-button"
    >
      Add Policy
    </Button>
  );

  const policyEditor = (
    <PolicyEditor
      type="policy"
      isEditing={isEditing}
      themeMode={themeMode}
      temporaryPolicy={isEditing ? temporaryPolicy : selectedPolicy}
      error={policyError}
      brandId={brandId}
      addNotification={addNotification}
      validateObject={validatePolicy}
      includeSdeAttribute={!!features?.['maven.feature.sdePage']}
      isComplexConditionEditor={isComplexConditionEditor}
      onClickAdvancedCondition={() => {
        setComplexConditionEditor(true);
        if (isEditing && !isComplexConditionEditor) {
          const v2Policy = convertV1PolicyToV2(temporaryPolicy);
          setSelectedPolicy(v2Policy);
          setTemporaryPolicy(v2Policy);
        }
      }}
      onClickSimpleCondition={() => {
        if (isEditing && isComplexConditionEditor) {
          try {
            const v1Policy = convertV2PolicyToV1(temporaryPolicy);
            setTemporaryPolicy(v1Policy);
            setSelectedPolicy(v1Policy);
            setComplexConditionEditor(false);
          } catch (err) {
            addNotification('Unable to convert to simple format', 'error');
          }
        }
      }}
      setTemporaryPolicy={(tmpPolicy) => {
        setTemporaryPolicy(tmpPolicy);
      }}
    />
  );

  const resultsEmpty = () => {
    if (!policies.length && !isFetchingPolicies && !!searchInput) {
      return (
        <ResultsEmpty text={(
          <>
            <Text pb={3} fontStyle="italic" color="midLightGrey">{`We couldn't find any matches for "${searchInput}". Try a different search term.`}</Text>
            <Flex alignItems="center" onClick={handleOnClose}>
              <IconBack style={{ cursor: 'pointer' }} />
              <Text fontWeight="bold" color="brightCerulean" style={{ cursor: 'pointer' }}>Back to all policies.</Text>
            </Flex>
          </>
        )}
        />
      );
    }

    if (!policies.length && !isFetchingPolicies && !searchInput && !totalPolicyCount) {
      return (
        <ResultsEmpty text={(
          <>
            <Text fontStyle="italic" color="midLightGrey">No policy has been created yet.</Text>
            <Link color="brightCerulean" style={{ textDecoration: 'none', fontStyle: 'normal' }} href={LINK_TO_DOCS} target="_blank" rel="noopener noreferrer">
              How to create a policy
            </Link>
          </>
        )}
        />
      );
    }
    return null;
  };

  return (
    <PolicyAnalyticsProvider>
      <Flex flexDirection="column" height="calc(100% - 40px)">
        <Box flex={1} minHeight={0} sx={{ display: 'flex', flexDirection: 'column' }}>
          <NavigateGuard
            when={isEditing}
            message="Are you sure you want to leave? Your changes will not be saved"
          />
          <Heading>{policyName}</Heading>
          <Subtitle mb={3}>{subtitle}</Subtitle>
          <Section
            section="routing-getting-started"
            title="Getting started"
            subtitle={(
              <Box>
                Building Intent &amp; Context Policies requires configuring several
                different features
                in the Conversational AI suite. To complete the remaining steps, please see the
                {' '}
                <Link href={LINK_TO_DOCS} target="_blank" rel="noopener noreferrer">
                  documentation
                </Link>
                .
              </Box>
            )}
          >
            <Stepper
              steps={[
                { id: 1, title: 'Manage attributes in Conversation Context Service' },
                { id: 2, title: 'Build routing policies' },
                {
                  id: 3,
                  title: (
                    <Box>
                      Connect to Maven using Maven Bot or
                      {' '}
                      <NavigationLink to="/ask-maven">AskMaven</NavigationLink>
                    </Box>
                  ),
                },
                { id: 4, title: 'Start using policies' },
              ]}
              activeStep={2}
            />
          </Section>

          <Box flex={1} minHeight={0} mt={4} pt={5} pb={5}>
            <PolicyManagementSection
              isEditing={isEditing}
              addPolicyButton={addPolicyButton}
              searchInput={!isEditing && (policies.length > 0 || searchInput) ? (
                <Box width="340px" height="32px">
                  <SearchInputV2 value={searchInput} placeholder="Search policy by name" onChange={handleSearchInputChange} onClear={handleOnClose} />
                </Box>
              ) : null}
              listHeader={(
                <PolicyListHeader
                  reorderButtons={(
                    <PolicyReorder
                      overallSelectedPolicyOrder={overallSelectedPolicyOrder}
                      onReorder={handlePolicyReorder}
                      isReordering={isReordering}
                      onReorderComplete={handleReorderComplete}
                      selectedPolicy={selectedPolicy}
                      totalPolicyCount={totalPolicyCount}
                      isFirstPolicyOnThePage={
                        (overallSelectedPolicyOrder - 1) % noOfRecordsPerPage === 0
                      }
                      isLastPolicyOnThePage={overallSelectedPolicyOrder % noOfRecordsPerPage === 0}
                    />
                  )}
                  isPoliciesLength={totalPolicyCount > 1}
                  isSearchInput={!!searchInput}
                  isEditing={isEditing}
                  isFetchingPolicies={isFetchingPolicies}
                />
              )}
              policyList={isFetchingPolicies
                ? (
                  <Flex key="loading" align="center" justifyContent="center" py={3} data-testid="loading-policies">
                    <Loading size={24} />
                  </Flex>
                ) : (
                  <>
                    {policies.map((policy) => (
                      <Box
                        key={policy.docId || 'new'}
                        ref={(el) => {
                          if (policy?.docId === selectedPolicy.docId) {
                            currentItem.current = el;
                          }
                        }}
                      >
                        <Row
                          themeMode={themeMode}
                          data={policy}
                          handleClick={handlePolicyClick}
                          handleChange={handleToggleChange}
                          isActive={isItemSelected(policy)}
                          useIcon
                          useOrdering
                          disableAutoScroll
                          isEditing={isEditing}
                        />
                      </Box>

                    ))}
                  </>
                )}
              detailHeader={isEditing ? editModeButtons : defaultButtons}
              policyDetail={
                (selectedPolicy && Object.keys(selectedPolicy).length > 0)
                  || (temporaryPolicy && Object.keys(temporaryPolicy).length > 0)
                  ? (
                    policyEditor
                  ) : null
              }
              resultsEmpty={resultsEmpty()}
              policyLength={policies.length}
            />
          </Box>
        </Box>
        {!isEditing && !isFetchingPolicies && policies.length > 0 && (
          <Box mt={5} flexShrink={0} minHeight="30px">
            <Pagination
              currentPageNumber={pageNumber}
              pageCount={totalPages}
              recordsPerPage={noOfRecordsPerPage}
              onChangeRecordsPerPage={handleRecordsPerPage}
              onChangePage={handlePageChange}
            />
          </Box>
        )}
        <ConfirmModal {...confirmModalProps} />
      </Flex>
    </PolicyAnalyticsProvider>
  );
}

PoliciesCcsV2.defaultProps = {
  subtitle: undefined,
};

PoliciesCcsV2.propTypes = {
  policyName: PropTypes.string.isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  type: PropTypes.string.isRequired,
};

export default PoliciesCcsV2;
